import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Form, Formik, Field, useFormik} from 'formik';
import {toast} from 'react-toastify';
import {logTime} from '../../services/tasks';

const EstimateInput = ({value, onChange, onBlur, isError, error}) => {
  return (
    <>
      <input
        type="text"
        className="form-control"
        name="estimateTime"
        placeholder={'Enter like 2h 30m'}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
      />
      {isError && error ? <p className="text-danger">{error}</p> : null}
    </>
  );
};

const LogTimeTrackingSection = ({
  loggedTimeValue,
  totalTimeValue,
  projectId,
  taskId,
  onLogTime
}) => {
  const loggedInId = useSelector(
    (state: any) => state?.auth?.currentUser?.data?._id
  );

  const [loggedTime, setLoggedTime] = useState(0);
  const [totalTime, setTotalTime] = useState(0); // Total time in minutes (e.g., 10 hours)

  React.useEffect(() => {
    setLoggedTime(loggedTimeValue);
    setTotalTime(totalTimeValue);
  }, []);
  const handleLogTime = () => {
    // Convert the input time (XhYm format) to minutes
    const [hours, minutes] = loggedTime.match(/\d+/g) || [0, 0];
    const inputTimeInMinutes = parseInt(hours, 10) * 60 + parseInt(minutes, 10);

    // Update the total time and logged time
    setTotalTime(totalTime + inputTimeInMinutes);
    setLoggedTime('');
  };

  const formatEstimateTime = (minutes) => {
    if (isNaN(minutes) || minutes < 0) {
      // console.log("*****formatEstimateTime 1******************", minutes)
      return 'N/A';
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours === 0 && remainingMinutes > 0) {
      return `${remainingMinutes}m`;
    } else if (hours > 0 && remainingMinutes === 0) {
      return `${hours}h`;
    } else if (hours > 0 && remainingMinutes > 0) {
      return `${hours}h${remainingMinutes}m`;
    }

    return '0m';
  };

  const calculateProgressBarColor = () => {
    const percentage = (loggedTime / totalTime) * 100;
    if (percentage < 70) {
      return 'bg-success';
    } else if (percentage < 90) {
      return 'bg-warning';
    } else {
      return 'bg-danger';
    }
  };

  function validateTaskEstimate(inputValue) {
    const regex = /^(\d{1,2}h(\d{1,2}m)?)?$/; // Regex to match XhYm format

    if (!inputValue.trim()) {
      return "Input can't be blank.";
    }

    if (!regex.test(inputValue)) {
      return 'Invalid format. Use XhYm, e.g., 2h30m.';
    }

    const [hours, minutes] = inputValue
      .split(/[hHmM]/)
      .filter(Boolean)
      .map(Number);

    // if (hours > 4) {
    //   return 'Maximum task duration is 4 hours.';
    // }

    if (minutes > 60) {
      return 'Minutes should not exceed 60.';
    }

    if (minutes < 10 && hours === 0) {
      return 'Minimum task duration is 10 minutes.';
    }

    return null; // Validation passed
  }

  const validate = (values) => {
    let errors = {};

    const resp = validateTaskEstimate(values.estimateTime);

    if (resp) {
      errors.estimateTime = resp;
    }

    return errors;
  };

  const onSubmit = async (values: any) => {
    try {
      const resp = await logTime({
        projectId: projectId,
        taskId: taskId,
        userId: loggedInId,
        timeSpent: values.estimateTime
      });
      if (resp.status !== 200) {
        return toast.error('Unable to log time');
      }

      toast.success('Time logged successfully');
      await onLogTime();
    } catch (error) {
      toast.error('Unable to log time');
    }
  };
  const {values, errors, touched, handleSubmit, handleBlur, handleChange} =
    useFormik({
      initialValues: {
        estimateTime: '0h0m'
      },
      enableReinitialize: true,
      validate,
      onSubmit
    });

  const getTimeRemaining = (totalTime, loggedTime) => {
    if (loggedTime > totalTime) {
      return (
        <div className="d-flex justify-content-between ">
          <span>{`Logged time: ${formatEstimateTime(loggedTime)}`}</span>
          <span>{`Total Estimate: ${formatEstimateTime(totalTime)}`}</span>
        </div>
      );
    }

    return (
      <div className="d-flex justify-content-between ">
        <span>{`Logged time: ${formatEstimateTime(loggedTime)}`}</span>
        <span>{`Hours remaining: ${formatEstimateTime(
          totalTime - loggedTime
        )}`}</span>
      </div>
    );
  };

  return (
    <div>
      <h4>Log Time Tracking</h4>
      <div>
        <div className="progress rounded">
          <div
            className={`progress-bar progress-bar-striped progress-bar-animated ${calculateProgressBarColor()}`}
            role="progressbar"
            style={{width: `${(loggedTime / totalTime) * 100}%`}}
          ></div>
        </div>
        {getTimeRemaining(totalTime, loggedTime)}
      </div>
      <div className="d-flex justify-content-between mt-3">
        <form className="d-flex w-100" onSubmit={handleSubmit}>
          <div className="input-group">
            <div className="d-flex  flex-column">
              <EstimateInput
                value={values.estimateTime}
                onChange={handleChange}
                onBlur={handleBlur}
                isError={touched.estimateTime}
                error={errors.estimateTime}
              />
            </div>
          </div>
          <div className="input-group">
            <button
              style={{height: '38px'}}
              className="btn btn-primary ml-2"
              type="submit"
            >
              Log Time
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LogTimeTrackingSection;
